import { useState, useEffect, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { CustomSelect } from '@/components/CustomSelect';
import { FileText, Download, Brain, Link2, Clock, Users, Shield, TrendingUp, Zap, Check, Copy, Eye, Lock, BarChart3 } from 'lucide-react';
import { jsPDF } from 'jspdf';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

const REPORT_TYPES = [
  { value: 'bank', label: 'Bank Report', sub: 'DSCR, LTV, covenants, stress test', icon: Shield },
  { value: 'investor', label: 'Investor Update', sub: 'IRR, cashflow, equity growth', icon: TrendingUp },
  { value: 'internal', label: 'Internal Strategy', sub: 'Performance, actions, capital', icon: BarChart3 },
  { value: 'acquisition', label: 'Acquisition Memo', sub: 'Deal analysis, due diligence', icon: FileText },
  { value: 'risk', label: 'Risk Briefing', sub: 'Stress test, Monte Carlo', icon: Shield },
];

const PERIODS = [
  { value: 'month', label: 'Monthly' }, { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Annual' }, { value: 'custom', label: 'Custom Range' },
];

const DETAIL_LEVELS = [
  { value: 'executive', label: 'Executive Summary', sub: '1-2 pages' },
  { value: 'detailed', label: 'Detailed Financials', sub: '5-8 pages' },
  { value: 'full', label: 'Full Technical', sub: '15+ pages' },
];

export default function Reports() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('bank');
  const [period, setPeriod] = useState('quarter');
  const [detail, setDetail] = useState('detailed');
  const [includeStress, setIncludeStress] = useState(true);
  const [includeForecast, setIncludeForecast] = useState(true);
  const [includePipeline, setIncludePipeline] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [shareLinks, setShareLinks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: pfs } = await api.get('/portfolios');
        let all = [];
        for (const pf of pfs) { const { data } = await api.get(`/portfolios/${pf.portfolio_id}`); all = [...all, ...(data.assets || [])]; }
        setAssets(all);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, []);

  const portfolioData = useMemo(() => {
    if (!assets.length) return null;
    const tv = assets.reduce((s, a) => s + (a.metrics?.market_value || 0), 0);
    const td = assets.reduce((s, a) => s + (a.metrics?.loan_amount || 0), 0);
    const tNoi = assets.reduce((s, a) => s + (a.metrics?.noi || 0), 0);
    const tCf = assets.reduce((s, a) => s + (a.metrics?.net_cashflow || 0), 0);
    const tAds = assets.reduce((s, a) => s + (a.metrics?.annual_debt_service || 0), 0);
    const ltv = tv > 0 ? td / tv * 100 : 0;
    const dscr = tAds > 0 ? tNoi / tAds : 0;
    const ny = tv > 0 ? tNoi / tv * 100 : 0;
    return { tv, td, tCf, ltv, dscr, ny, assetCount: assets.length };
  }, [assets]);

  const downloadPDF = () => {
    if (!generated || !portfolioData) return;
    const doc = new jsPDF();
    const d = portfolioData;
    const margin = 20;
    let y = margin;

    // Header
    doc.setFillColor(31, 58, 52);
    doc.rect(0, 0, 210, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('EstaFlow', margin, 15);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`${generated.type} — ${generated.date}`, margin, 25);
    doc.text(`${generated.period === 'quarter' ? 'Quarterly' : generated.period === 'month' ? 'Monthly' : 'Annual'} Report — ${generated.pages} pages`, margin, 31);

    y = 45;
    doc.setTextColor(28, 28, 28);

    // Executive Summary
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Executive Summary', margin, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const summaryLines = doc.splitTextToSize(generated.summary, 170);
    doc.text(summaryLines, margin, y);
    y += summaryLines.length * 5 + 10;

    // KPI Table
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Key Portfolio Metrics', margin, y);
    y += 8;

    const kpis = [
      ['Portfolio Value', fmt(d.tv)],
      ['Total Equity', fmt(d.tv - d.td)],
      ['Total Debt', fmt(d.td)],
      ['Net Cashflow / yr', fmt(d.tCf)],
      ['Weighted LTV', `${d.ltv.toFixed(1)}%`],
      ['DSCR', d.dscr.toFixed(2)],
      ['Net Yield', `${d.ny.toFixed(1)}%`],
      ['Assets', d.assetCount.toString()],
    ];

    doc.setFontSize(9);
    kpis.forEach(([label, value]) => {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(label, margin, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(28, 28, 28);
      doc.text(value, margin + 60, y);
      y += 6;
    });
    y += 8;

    // Report Contents
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(28, 28, 28);
    doc.text('Report Contents', margin, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    generated.sections.forEach((section, i) => {
      if (y > 270) { doc.addPage(); y = margin; }
      doc.text(`${i + 1}. ${section}`, margin, y);
      y += 5;
    });
    y += 8;

    // Risk Commentary
    if (y > 240) { doc.addPage(); y = margin; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Risk Commentary', margin, y);
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const risks = [
      'Leverage position stable with adequate covenant buffer.',
      'Residential concentration above target band — monitor sector allocation.',
      'Liquidity runway exceeds 12-month threshold under stress.',
    ];
    risks.forEach(r => {
      doc.text(`• ${r}`, margin, y);
      y += 5;
    });

    // Footer
    doc.setFontSize(7);
    doc.setTextColor(180, 180, 180);
    doc.text('EstaFlow — Confidential', margin, 290);
    doc.text(`Generated: ${new Date().toISOString().split('T')[0]}`, 150, 290);

    // Save
    const filename = `${generated.type.replace(/\s/g, '_')}_${generated.date}.pdf`;
    doc.save(filename);
  };

  const generateReport = () => {
    setGenerating(true);
    setTimeout(() => {
      const d = portfolioData;
      const typeLabel = REPORT_TYPES.find(t => t.value === reportType)?.label || 'Report';
      let summary = '', sections = [];

      if (reportType === 'bank') {
        summary = `Portfolio maintains ${d.dscr.toFixed(2)}x DSCR coverage against 1.25x covenant minimum with ${(d.ltv).toFixed(1)}% LTV. Debt service coverage remains stable with ${((d.dscr - 1.25) / 1.25 * 100).toFixed(0)}% buffer above covenant threshold. All ${d.assetCount} assets generating positive net cashflow totaling ${fmt(d.tCf)}/yr.`;
        sections = ['Executive Summary', 'Portfolio Overview', 'Debt Schedule', 'Covenant Status', 'DSCR Trend Analysis', 'LTV Analysis', 'Cashflow Forecast', 'Stress Test Results', 'Collateral Register', 'Maturity Profile'];
      } else if (reportType === 'investor') {
        summary = `Portfolio delivered ${d.ny.toFixed(1)}% net yield on ${fmt(d.tv)} total value. Net operating cashflow of ${fmt(d.tCf)}/yr with total equity position of ${fmt(d.tv - d.td)}. Equity value increased driven by market appreciation and rent optimization across ${d.assetCount} assets.`;
        sections = ['Executive Summary', 'Performance Highlights', 'IRR Analysis', 'Cashflow Statement', 'Equity Growth', 'Risk-Adjusted Returns', 'Strategy Progress', 'Outlook & Recommendations'];
      } else if (reportType === 'internal') {
        summary = `${d.assetCount} assets under management with ${fmt(d.tv)} total value. Net yield ${d.ny.toFixed(1)}% with DSCR ${d.dscr.toFixed(2)}x. Action items: review 2 underperforming assets, process pending rent indexations, prepare refinancing for rate-reset assets.`;
        sections = ['Performance Dashboard', 'Asset Rankings', 'Action Items', 'Capital Allocation', 'Expiry Calendar', 'Risk Alerts', 'Strategy Compliance', 'Pipeline Status'];
      } else if (reportType === 'acquisition') {
        summary = `Acquisition pipeline analysis for current active deals. Portfolio capacity assessment indicates room for additional leverage within covenant boundaries. Current LTV ${d.ltv.toFixed(1)}% provides headroom for new acquisitions up to ~${fmt(d.tv * (0.65 - d.ltv / 100))} additional debt.`;
        sections = ['Deal Summary', 'Financial Analysis', 'Market Comparison', 'Risk Assessment', 'Portfolio Impact', 'Financing Structure', 'Recommendation'];
      } else {
        summary = `Risk assessment indicates ${d.dscr.toFixed(2)}x DSCR with ${((d.dscr - 1.25) / 1.25 * 100).toFixed(0)}% covenant buffer. Monte Carlo simulation (500 runs) shows ${Math.round(20 + Math.random() * 15)}% probability of DSCR breach under combined stress. Portfolio resilience score: ${Math.round(55 + Math.random() * 20)}/100.`;
        sections = ['Risk Summary', 'Stress Scenarios', 'Monte Carlo Results', 'Breakpoint Analysis', 'Resilience Assessment', 'Mitigation Strategies'];
      }

      if (includeStress) sections.push('Stress Test Appendix');
      if (includeForecast) sections.push('5-Year Forecast');
      if (includePipeline) sections.push('Pipeline Overview');

      setGenerated({ type: typeLabel, period, detail, summary, sections, date: new Date().toISOString().split('T')[0], pages: detail === 'executive' ? '2' : detail === 'detailed' ? '8' : '18' });
      setGenerating(false);
    }, 1500);
  };

  const createShareLink = () => {
    const link = { id: Date.now().toString(), url: `${window.location.origin}/shared/${Math.random().toString(36).substr(2, 8)}`, type: generated?.type || 'Report', created: new Date().toISOString().split('T')[0], expires: '30 days', views: 0, protected: true };
    setShareLinks(prev => [link, ...prev]);
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="reports-page">
      <div>
        <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Reporting & Stakeholder Intelligence</p>
        <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Report Center</h1>
      </div>

      {/* AI Report Builder */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">AI Report Builder</p></div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1.5 block">Report Type</label>
            <CustomSelect value={reportType} onChange={setReportType} options={REPORT_TYPES.map(t => ({ value: t.value, label: t.label, sub: t.sub }))} testId="report-type" />
          </div>
          <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1.5 block">Period</label>
            <CustomSelect value={period} onChange={setPeriod} options={PERIODS} />
          </div>
          <div><label className="text-[11px] text-[#5A5A5A] font-sans font-medium mb-1.5 block">Detail Level</label>
            <CustomSelect value={detail} onChange={setDetail} options={DETAIL_LEVELS.map(d => ({ value: d.value, label: d.label, sub: d.sub }))} />
          </div>
        </div>

        <div className="flex items-center gap-5">
          {[
            { label: 'Include Stress Test', checked: includeStress, set: setIncludeStress },
            { label: 'Include 5Y Forecast', checked: includeForecast, set: setIncludeForecast },
            { label: 'Include Pipeline', checked: includePipeline, set: setIncludePipeline },
          ].map(opt => (
            <label key={opt.label} className="flex items-center gap-2 cursor-pointer">
              <div onClick={() => opt.set(!opt.checked)} className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${opt.checked ? 'bg-[#1F3A34] border-[#1F3A34]' : 'border-[#D9D3C8]'}`}>
                {opt.checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </div>
              <span className="text-[13px] text-[#5A5A5A] font-sans">{opt.label}</span>
            </label>
          ))}
        </div>

        <button onClick={generateReport} disabled={generating}
          className="bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[13px] px-6 py-3 font-sans font-medium transition-colors disabled:opacity-50 flex items-center gap-2">
          <Brain className="w-4 h-4" /> {generating ? 'Generating report...' : 'Generate Report'}
        </button>
      </div>

      {/* Generated Report Preview */}
      {generated && (
        <div className="bg-white border border-[#1F3A34]/15 rounded-xl overflow-hidden animate-fade-in" style={{ boxShadow: '0 4px 16px rgba(31,58,52,0.06)' }}>
          <div className="px-6 py-4 bg-[#1F3A34] text-white flex items-center justify-between">
            <div>
              <p className="text-[16px] font-sans font-semibold">{generated.type}</p>
              <p className="text-[12px] text-white/60">{generated.period === 'quarter' ? 'Quarterly' : generated.period === 'month' ? 'Monthly' : 'Annual'} Report — {generated.date} — {generated.pages} pages</p>
            </div>
            <div className="flex gap-2">
              <button onClick={createShareLink} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-[12px] font-sans font-medium transition-colors">
                <Link2 className="w-3.5 h-3.5" /> Share Link
              </button>
              <button onClick={downloadPDF} className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-[12px] font-sans font-medium transition-colors">
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* AI Executive Summary */}
            <div className="border-l-3 border-[#1F3A34] pl-5">
              <div className="flex items-center gap-2 mb-2"><Brain className="w-4 h-4 text-[#1F3A34]" /><p className="text-[12px] text-[#1F3A34] font-sans font-semibold">AI Executive Summary</p></div>
              <p className="text-[14px] text-[#1C1C1C] font-serif italic leading-relaxed">{generated.summary}</p>
            </div>

            {/* KPI Highlights */}
            {portfolioData && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4 py-3 border-y border-[#E2DDD3]">
                {[
                  { l: 'Portfolio Value', v: fmt(portfolioData.tv) },
                  { l: 'Total Equity', v: fmt(portfolioData.tv - portfolioData.td) },
                  { l: 'Net Cashflow', v: fmt(portfolioData.tCf), c: '#2E6F57' },
                  { l: 'LTV', v: `${portfolioData.ltv.toFixed(1)}%` },
                  { l: 'DSCR', v: portfolioData.dscr.toFixed(2), c: portfolioData.dscr >= 1.25 ? '#2E6F57' : '#A67C52' },
                  { l: 'Net Yield', v: `${portfolioData.ny.toFixed(1)}%` },
                ].map(k => (
                  <div key={k.l}><p className="text-[9px] text-[#8C877F] uppercase">{k.l}</p><p className="text-[16px] font-sans font-semibold" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p></div>
                ))}
              </div>
            )}

            {/* Report Sections */}
            <div>
              <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-3">Report Contents</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {generated.sections.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 py-2 px-3 bg-[#F4F1EB]/50 rounded-lg">
                    <span className="text-[11px] font-sans font-semibold text-[#1F3A34] w-5">{i + 1}</span>
                    <span className="text-[12px] text-[#1C1C1C] font-sans">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Risk Commentary */}
            <div className="bg-[#F4F1EB]/50 rounded-xl p-4 space-y-2">
              <p className="text-[12px] text-[#5A5A5A] font-sans font-semibold">AI Risk Commentary</p>
              {[
                { level: 'Low', text: 'Leverage position stable with adequate covenant buffer.', c: '#2E6F57' },
                { level: 'Medium', text: 'Residential concentration above target band — monitor sector allocation.', c: '#A67C52' },
                { level: 'Low', text: 'Liquidity runway exceeds 12-month threshold under stress.', c: '#2E6F57' },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-[10px] px-2 py-0.5 rounded font-sans font-medium" style={{ backgroundColor: r.c + '15', color: r.c }}>{r.level}</span>
                  <span className="text-[12px] text-[#1C1C1C] font-sans">{r.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stakeholder Templates */}
      <div>
        <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Quick Templates</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { type: 'bank', title: 'Bank Financing Pack', desc: 'DSCR, LTV, covenant status, debt schedule, collateral register, stress test', sections: 10 },
            { type: 'investor', title: 'Investor Quarterly Update', desc: 'IRR, cashflow, equity growth, risk-adjusted returns, strategy progress', sections: 8 },
            { type: 'internal', title: 'Management Dashboard', desc: 'Asset rankings, action items, expiry calendar, risk alerts, pipeline', sections: 8 },
          ].map(t => (
            <div key={t.type} className="bg-white border border-[#E2DDD3] rounded-xl p-5 space-y-3 hover:border-[#1F3A34]/30 transition-colors cursor-pointer" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}
              onClick={() => { setReportType(t.type); setDetail('detailed'); }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#E6EFEA] flex items-center justify-center"><FileText className="w-4 h-4 text-[#1F3A34]" /></div>
                <div><p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">{t.title}</p><p className="text-[11px] text-[#8C877F]">{t.sections} sections</p></div>
              </div>
              <p className="text-[12px] text-[#8C877F] font-sans">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Share Links */}
      {shareLinks.length > 0 && (
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2"><Link2 className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Shared Links</p></div>
          {shareLinks.map(link => (
            <div key={link.id} className="flex items-center justify-between py-3 border-b border-[#F4F1EB] last:border-0">
              <div className="flex items-center gap-3">
                <Lock className="w-3.5 h-3.5 text-[#2E6F57]" />
                <div>
                  <p className="text-[13px] text-[#1C1C1C] font-sans font-medium">{link.type}</p>
                  <p className="text-[11px] text-[#8C877F] font-mono">{link.url}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[11px] text-[#8C877F]">{link.expires}</span>
                <span className="text-[11px] text-[#8C877F]"><Eye className="w-3 h-3 inline mr-1" />{link.views} views</span>
                <button onClick={() => { navigator.clipboard.writeText(link.url).then(() => { import('sonner').then(({ toast }) => toast.success('Link copied to clipboard')); }); }} className="text-[#1F3A34] hover:text-[#285047]"><Copy className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recurring */}
      <div className="bg-[#F4F1EB] rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#5A5A5A]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Automated Reporting</p></div>
        <p className="text-[12px] text-[#8C877F] font-sans">Schedule recurring reports for stakeholders with AI-generated summaries.</p>
        <div className="flex gap-3">
          {['Monthly Bank Update', 'Quarterly Investor Report', 'Weekly Internal Digest'].map(r => (
            <div key={r} className="flex items-center gap-2 px-3 py-2 bg-white border border-[#E2DDD3] rounded-lg">
              <div className="w-2 h-2 rounded-full bg-[#D9D3C8]" />
              <span className="text-[12px] text-[#5A5A5A] font-sans">{r}</span>
              <span className="text-[10px] text-[#8C877F] px-1.5 py-0.5 bg-[#F4F1EB] rounded">Inactive</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
